import AppAlert from '@nxsys/components/AppAlert';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppRateForm, { AppRateFormValues } from '@nxsys/components/AppRateForm';
import AppRateTable from '@nxsys/components/AppRateTable';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  PaymentFrequencyEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import {
  useGetTimesheetById,
  useUpdateTimesheetById,
} from '@nxsys/hooks/api/timesheets';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetTimesheetEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { Rate, TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import {
  formatStatus,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { getTaxWeekTaxMonthOptionByFrequency } from '@nxsys/utils/taxYear';
import { Descriptions, Form, Spin, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, isEmpty, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
  readOnly?: boolean;
};

const Timesheet: React.FC<Props> = ({ employeeId, readOnly = false }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const { payrollCountry, isBureau, isEmployee } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { timesheetList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = timesheetList;

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<string | null>(
    null,
  );

  // Detail view hooks
  const [rateForm] = Form.useForm<AppRateFormValues>();
  const {
    run: getTimesheetById,
    value: timesheetValue,
    loading: isGetTimesheetByIdPending,
  } = useGetTimesheetById();
  const {
    run: updateTimesheetById,
    value: updateTimesheetValue,
    loading: isUpdateTimesheetByIdPending,
  } = useUpdateTimesheetById();

  const timesheetData = useMemo(
    () => (updateTimesheetValue ?? timesheetValue)?.data,
    [timesheetValue, updateTimesheetValue],
  );
  const isGeneratedPayroll = useMemo(
    () =>
      !!timesheetData?.newPayroll ||
      (!!timesheetData?.newDraftPayroll &&
        isEmpty(timesheetData?.newDraftPayroll?.errors)),
    [timesheetData],
  );
  const disabledTimesheet = useMemo(
    () =>
      timesheetData?.approveStatus === TimesheetStatusEnum.CANCEL ||
      isGeneratedPayroll ||
      timesheetData?.isEditable === false ||
      isEmployee ||
      readOnly,
    [timesheetData, isGeneratedPayroll, isEmployee, readOnly],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxWeekTaxYear.taxYear,
    });
  }, []);

  useEffect(() => {
    if (selectedTimesheetId) {
      getTimesheetById({ id: selectedTimesheetId });
    }
  }, [selectedTimesheetId]);

  const handleFetchData = (params: GetTimesheetEmployeeListParams) => {
    const newParams = { ...params, id: employeeId, notHasAgency: true };
    dispatch(employeeActions.getTimesheetEmployeeList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const handleViewDetail = useCallback((id: string) => {
    setSelectedTimesheetId(id);
  }, []);

  const refetchList = () => {
    handleFetchData({ ...queryParams });
  };

  const handleBackToList = () => {
    setSelectedTimesheetId(null);
  };

  const handleSubmitRate = async (values: AppRateFormValues) => {
    if (!selectedTimesheetId) return;
    const payload = {
      rates: values.rates?.map(
        (rate) =>
          ({
            ...timesheetData?.rates?.find((r) => r.rateId === rate.rateId),
            rateId: rate.rateId ?? '',
            rateCode: rate.rateCode?.value ?? '',
            rateType: rate.rateType ?? '',
            description: rate.description ?? '',
            rate: rate.rate ?? '',
            units: toNumber(rate.units),
            vatRate: rate.vatRate ?? '',
            vatDetails: rate.vatDetails?.value ?? '',
            net: toNumber(rate.net),
            total: toNumber(rate.total),
          }) as Rate,
      ),
    };
    await updateTimesheetById({
      id: selectedTimesheetId,
      payload,
    }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.updateSuccess'],
      });
      refetchList();
    });
  };

  const columns: ColumnsType<TimeSheetDataItem> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleViewDetail(record?.id)}>
            <Tooltip title="Edit">
              <span><Icons.IconEdit /></span>
            </Tooltip>
          </li>
          {record?.file && (
            <li onClick={() => window.open(record.file, '_blank')}>
              <Tooltip title="Download">
                <span><Icons.IconDownload /></span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'timesheetId',
      title: messages['timesheet.timesheetId'],
      dataIndex: 'id',
    },
    {
      key: 'invoiceRef',
      title: messages['common.invoiceRef'],
      dataIndex: 'invoiceNo',
    },
    {
      key: 'createdAt',
      title: messages['common.createdAt'] ?? 'Created',
      dataIndex: 'createdAt',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value
          ? moment(value).tz('Europe/London').format('DD/MM/YYYY HH:mm')
          : '_',
    },
    {
      key: 'batch',
      title: messages['timesheet.batch'],
    },
    ...((!isBureau
      ? [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'agencyRef',
            render: (value) => value?.name ?? '-',
          },
        ]
      : []) as ColumnsType<TimeSheetDataItem>),
    {
      key: 'startDate',
      title: messages['common.startDate'],
      dataIndex: 'startDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      dataIndex: 'totalNet',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      dataIndex: 'totalVat',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      dataIndex: 'totalPrices',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'receipt',
      title: messages['timesheet.receipt'],
      dataIndex: 'receipt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxWeek'],
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        `${record?.taxPeriod}/${record?.taxYear || record?.currentYear}`,
    },
    {
      key: 'totalPaid',
      title: messages['common.totalPaid'],
      dataIndex: 'totalPaid',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'periodPaid',
      title: messages['common.periodPaid'],
      dataIndex: 'periodPaid',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'outstanding',
      title: messages['common.outstanding'],
      dataIndex: 'outstanding',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const handleExport = (data: TimeSheetDataItem[]) => {
    const dataExport = data.map((item) => ({
      id: item?.id || '',
      invoiceNo: item?.invoiceNo || '',
      batch: item?.batch || '',
      agencyName: item?.agencyRef?.name || '',
      startDate: item?.startDate
        ? moment(item?.startDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      endDate: item?.endDate
        ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      totalNet: formatMoney(
        toNumber(item?.totalNet) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      totalVat: formatMoney(
        toNumber(item?.totalVat) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      totalPrices: formatMoney(
        toNumber(item?.totalPrices) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      receipt: formatMoney(
        toNumber(item?.receipt) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      taxPeriod: item?.taxPeriod || '',
      taxYear: item?.taxYear || item?.currentYear || '',
      totalPaid: formatMoney(
        toNumber(item?.totalPaid) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      periodPaid: formatMoney(
        toNumber(item?.periodPaid) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      outstanding: formatMoney(
        toNumber(item?.outstanding) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
    }));

    const headers = [
      'Timesheet ID',
      'Invoice Ref',
      'Batch #',
      'Agency Name',
      'Start Date',
      'End Date',
      'Net',
      'VAT',
      'Total',
      'Receipt',
      'Tax Period',
      'Tax Year',
      'Total Paid',
      'Period Paid',
      'Outstanding',
    ];

    const exportName = csvConfigExport(`TimeSheet-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  // Detail View
  if (selectedTimesheetId) {
    return (
      <Spin
        spinning={isGetTimesheetByIdPending || isUpdateTimesheetByIdPending}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-x-3">
            <AppBackButton onClick={handleBackToList} />
            <Typography.Title
              level={5}
              className="!mb-0 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
            >
              {selectedTimesheetId}
            </Typography.Title>
            <AppTagStatus
              className={`tag-popup ${formatStatus(timesheetData?.status ?? '')}`}
            >
              {timesheetData?.status}
            </AppTagStatus>
          </div>
          {!disabledTimesheet && timesheetData && (
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => rateForm.submit()}
            />
          )}
        </div>

        {isGeneratedPayroll && (
          <AppAlert
            className="mb-4"
            type="warning"
            variant="warning"
            message={messages['timesheet.onlyAllowViewTimesheet']}
          />
        )}

        <AppCard className="mb-4" title={messages['timesheet.title']}>
          <AppDescription
            column={1}
            className="p-4"
            colon={false}
            labelStyle={{ width: '200px' }}
          >
            <Descriptions.Item label={messages['common.subcontractorName']}>
              {timesheetData?.subcontractorRef?.fullName ??
                `${timesheetData?.subcontractorRef?.forename ?? ''} ${timesheetData?.subcontractorRef?.surname ?? ''}`}
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.assignment']}>
              {timesheetData?.assignmentRef?.assignmentName ?? '-'}
            </Descriptions.Item>
            {!isBureau && (
              <Descriptions.Item label={messages['common.agencyName']}>
                {timesheetData?.agencyRef?.name ?? '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={messages['timesheet.timesheetPeriod']}>
              {timesheetData?.startDate
                ? moment(timesheetData?.startDate).format('DD MMM')
                : ''}
              {' - '}
              {timesheetData?.endDate
                ? moment(timesheetData?.endDate).format('DD MMM')
                : ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.siteId']}>
              {timesheetData?.siteId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.taxPeriod']}>
              {
                getTaxWeekTaxMonthOptionByFrequency(
                  timesheetData?.subcontractorRef?.payFrequency ??
                    PaymentFrequencyEnum.WEEKLY,
                  timesheetData?.subcontractorRef?.payFrequency ===
                    PaymentFrequencyEnum.MONTHLY
                    ? (timesheetData?.monthConfig ?? 0)
                    : (timesheetData?.taxPeriodConfig ?? 0),
                )?.label
              }
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.attachment']}>
              {timesheetData?.file ? (
                <AppUploadFile
                  urlFile={timesheetData?.file ?? ''}
                  onChange={(_: File) => {
                    console.info('file', _);
                  }}
                  hideDelete
                  onClickFile={() =>
                    window.open(timesheetData?.file ?? '', '_blank')
                  }
                />
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </AppDescription>
        </AppCard>

        {!isGetTimesheetByIdPending ? (
          !disabledTimesheet && timesheetData ? (
            <AppRateForm
              form={rateForm}
              onFinish={handleSubmitRate}
              initialValues={{
                rates: (timesheetData?.rates ?? [])?.map((rate) => ({
                  ...rate,
                  rateCode: {
                    label: rate.rateCode ?? '',
                    value: rate.rateCode ?? '',
                  },
                  vatDetails: {
                    label: rate.vatDetails ?? '',
                    value: rate.vatDetails ?? '',
                  },
                  net: rate.net?.toString() ?? '',
                  total: rate.total?.toString() ?? '',
                  units: rate.units?.toString() ?? '',
                })),
              }}
              listDisabled={['net', 'total', 'rateType', 'description']}
              disabledDelete={disabledTimesheet}
              disabledAdd={disabledTimesheet}
              subcontractor={timesheetData?.subcontractorRef}
            />
          ) : (
            <AppRateTable data={timesheetData?.rates ?? []} />
          )
        ) : null}
      </Spin>
    );
  }

  // List View
  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.timesheet']}
      </Typography.Title>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
        <div className="flex items-center gap-x-2">
          <TaxYearSelect
            initValue={currentTaxWeekTaxYear.taxYear}
            onFilter={(value) =>
              handleFetchData({ ...queryParams, taxYear: value.taxYear })
            }
          />
          <AppButton
            name={messages['common.export']}
            variant="outlined"
            leftIcon={<Icons.IconArrowExport />}
            onClick={handleExportSelected}
          />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2500, y: 700 }}
          titleEmpty={messages['timesheet.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
    </div>
  );
};

export default Timesheet;
